import React, { useContext, useEffect, useState } from 'react';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFTextArea from 'src/components/controls/tempRHFControls/RHFTextArea';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import PersonnelTaxIRSCategory from 'src/models/personnel/PersonnelTaxIRSCategory';
import { useForm } from 'react-hook-form';
import useFetch from 'use-http';
import moment from 'moment';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import PersonnelToast from '../../components/PersonnelToast';
import TSDILandman from 'src/models/personnel/TSDILandman';

interface IUpdateIRSCategoryPanelProps {
    closePanel: (newCatType: string, newEffDate: Date) => void;
    irsDropdownOptions: Array<DropDownSelection>;
    currentIrsCat: string;
    selectedPersonnel: TSDILandman;
}

type DropDownSelection = {
    code: string,
    dscr: string
};

const UpdateIRSCategoryPanel = (props: IUpdateIRSCategoryPanelProps) => {
    const { handleSubmit, register, errors, getValues, setValue } = useForm();
    const { get, post, response } = useFetch();
    const [oldPeriodNo, setOldPeriodNo] = useState<string>('');

    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);

    const appContext = useContext<IAppContextState>(AppContext);
    const currentUser = {
        name: appContext.currentUser.username,
        id: appContext.currentUser.userId,
        lmsNumber: appContext.currentUser.lmsNumber
    };

    const effectivePeriodNoRegex = /^\d{4}-\d{2}$/;

    useEffect(() => {
        setValue('currentCategory', props.currentIrsCat);
    }, [props.currentIrsCat]);

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color  });
    };

    const irsCategoryTypeDropdown = () => {
        return props.irsDropdownOptions
            ? props.irsDropdownOptions?.map((ct: DropDownSelection) => {
                return { value: ct.code, label: ct.dscr };
            })
            : [];
    };

    const updateEffectivePeriodDate = async () => {
        let newPeriodNo = getValues('effPeriodNo');
        if (!effectivePeriodNoRegex.test(newPeriodNo)) {
            if (newPeriodNo.lengh >= 7) {
                showToast('Ensure new period has the proper format: YYYY-MM', `rgba(255,0,0,0.5)`);
            }
            return null;
        }
        
        if (newPeriodNo === oldPeriodNo) {
            return null;
        }

        let formattedNo = parseInt(newPeriodNo.replace('-', ''), 10);

        let result = await get(`/v1.0/landmen/personnel/${formattedNo}/tax-period-date`);
        if (response.ok) {
            let formattedDate = result ? (moment(result).format('YYYY-MM-DD') as unknown as Date) : null;
            setValue('effDate', formattedDate);
            setOldPeriodNo(newPeriodNo);
            return result;
        } else {
            showToast('Unable to update effective date: ' + result.message, `rgba(255,0,0,0.5)`);
            return null;
        }
    };

    const submitData = async (data: PersonnelTaxIRSCategory) => {
        // previous category type
        let prevIrsCatType = getValues('currentCategory') === 'NEEDS TO BE DEFINED' ? 'NA' : getValues('currentCategory');
        if (prevIrsCatType !== 'NA' && prevIrsCatType !== null) {
            let irsCode = irsCategoryTypeDropdown().find((cat) => cat.label === prevIrsCatType)?.value;
            data.prevIrsCatType = irsCode;
        } else {
            data.prevIrsCatType = prevIrsCatType;
        }

        // tran by information
        if (currentUser.lmsNumber !== null) {
            data.tranByNo = parseInt(currentUser?.lmsNumber?.toString(), 10);
        } else {
            console.log('no personnel found or no lms number found: ', currentUser);
        }

        data.tranBy = currentUser.name;
        
        // effective period 
        let finalPeriodNo = getValues('effPeriodNo');
        let formattedNo = parseInt(finalPeriodNo.replace('-', ''), 10);
        data.effPeriodNo = formattedNo;
       
        // other data
        data.effDate = new Date(getValues('effDate'));
        data.notes = getValues('notes');
        data.lmNo = props.selectedPersonnel.lmNo; // note - confirmed lmNo and not lmsNumber / tsdNo

        await post(`/v1.0/landmen/personnel/${props.selectedPersonnel.tsdNo}/create-tax-history`, data);

        if (response.ok) {
            props.closePanel(data.irsCatType, data.effDate);
        } else {
            showToast('Unable to update irs category: ' + response, `rgba(255,0,0,0.5)`);
        }
    };

    return (
        <div>
            <div
                style={{
                    borderBottom: '1px solid #ccc',
                    margin: '-.5rem -.5rem 1rem -.5rem',
                    background: '#EFF4F8',
                    display: 'flex',
                    justifyContent: 'left'
                }}
            >
                <h2 style={{ color: '#6393C1', padding: '.5rem 1rem', margin: '0' }}>Update IRS Category</h2>
            </div>
            <div className="mb-2">
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
                <form onSubmit={handleSubmit(submitData)}>
                    <RHFInputBox
                        name={'currentCategory'}
                        label={'Current IRS Cat.'}
                        labelOnLeft={true}
                        register={register}
                        errors={errors}
                        disabled={true}
                        isRequired={true}
                        defaultVal={props.currentIrsCat}
                    />
                    <RHFSelect
                        name={'irsCatType'}
                        label={'IRS Category Type'}
                        labelOnLeft={true}
                        register={register}
                        errors={errors}
                        isRequired={true}
                        options={irsCategoryTypeDropdown()}
                    />
                    <RHFInputBox
                        name={'effPeriodNo'}
                        label={'Eff Period No.'}
                        labelOnLeft={true}
                        register={register}
                        errors={errors}
                        isRequired={true}
                        onChange={() => updateEffectivePeriodDate()}
                    />
                    <RHFInputBox
                        name={'effDate'}
                        label={'Eff Date.'}
                        type="date"
                        labelOnLeft={true}
                        register={register}
                        errors={errors}
                        disabled={true}
                    />
                    <div className="form-row">
                        <label>Notes / Remarks</label>
                        <RHFTextArea
                            name={'notes'}
                            register={register}
                            errors={errors}
                        />
                    </div>
                </form>
            </div>
            <div className="button-row" style={{ paddingBottom: '1rem' }}>
                <Button text="Save" onClick={handleSubmit(submitData)} intent="primary" />
                <Button text="Cancel" onClick={() => props.closePanel('', null)} intent="secondary" />
            </div>
        </div >
    );
};

export default UpdateIRSCategoryPanel;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/taxInfo/UpdateIRSCategoryPanel.tsx